import { useState, useEffect, useCallback, useRef, Component } from "react";
import api from "@/lib/api";
import { ltpTag, stampRx, priceLabel } from "@/lib/priceLabel";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import ScannerLink from "@/components/ScannerLink";
import { UsageMeter } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Zap, TrendingUp, TrendingDown, RefreshCw, ChevronDown, ChevronUp,
  Target, BarChart2, Brain, AlertTriangle, Play, Loader2, Flame,
  Check, X, Clock, Star, Activity, Copy,
} from "lucide-react";

// RU-MCA: copy a signal as plain text for manual entry in a broker app. The text itself is
// built by the backend (services/manual_trade.copy_text) so it has one tested formatter.
// The async Clipboard API is tried first; when it is missing (plain http, older browsers) or rejects
// (permission, focus), a hidden textarea and document.execCommand("copy") are tried. Only when both fail
// does the caller see an error.
function copyViaTextarea(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    if (!document.execCommand("copy")) throw new Error("copy refused");
  } finally {
    document.body.removeChild(ta);
  }
}

async function copyToClipboard(text) {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // fall through to the textarea fallback
    }
  }
  copyViaTextarea(text);
}

// ── Signal Dashboard ──────────────────────────────────────────────────────────

export function SignalDashboard({ signals, expectancy, total, scanInterval, lastScan }) {
  const [open, setOpen] = useState(true);

  const byType = signals.reduce((acc, s) => {
    acc[s.instrument_type] = (acc[s.instrument_type] || 0) + 1;
    return acc;
  }, {});
  const bullish = signals.filter((s) => s.direction === "BULLISH").length;
  const bearish  = signals.filter((s) => s.direction === "BEARISH").length;
  const avgConf = signals.length
    ? Math.round(signals.reduce((s, x) => s + (x.confidence_score || 0), 0) / signals.length * 100)
    : null;

  const scanners = Object.entries(expectancy).map(([key, e]) => ({
    key,
    label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    signals: e.total_signals ?? 0,
    winRate: e.win_rate_pct,
    rr: e.avg_risk_reward,
    expectancy: e.expectancy,
  })).sort((a, b) => (b.expectancy ?? -99) - (a.expectancy ?? -99));

  const biasTotal = bullish + bearish || 1;
  const bullPct = Math.round(bullish / biasTotal * 100);

  return (
    <div className="panel overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-3 text-xs font-section tracking-wider txt-secondary hover:text-[var(--text-primary)] transition-colors"
      >
        <span className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" />
          SIGNAL ENGINE DASHBOARD
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="border-t border-[var(--border)] p-4 space-y-4">
          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">ACTIVE SIGNALS</div>
              <div className="text-2xl font-mono-data font-bold text-[var(--text-primary)]">{total}</div>
              <div className="flex gap-2 text-[10px] font-mono-data">
                <span className="text-blue-400">FUT {byType.FUT || 0}</span>
                <span className="text-purple-400">OPT {byType.OPT || 0}</span>
                <span className="txt-muted">EQ {byType.EQ || 0}</span>
              </div>
            </div>
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">AVG CONFIDENCE</div>
              <div className={`text-2xl font-mono-data font-bold ${avgConf >= 70 ? "txt-profit" : avgConf >= 50 ? "text-amber-400" : "txt-loss"}`}>
                {avgConf != null ? `${avgConf}%` : "—"}
              </div>
              <div className="text-[10px] txt-muted font-section">across active signals</div>
            </div>
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">MARKET BIAS</div>
              <div className="flex items-end gap-1">
                <span className="text-2xl font-mono-data font-bold txt-profit">{bullish}</span>
                <span className="txt-muted text-sm font-mono-data mb-0.5">/ {bearish}</span>
              </div>
              <div className="h-1.5 bg-[var(--bg-surface)] rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${bullPct}%` }} />
              </div>
              <div className="text-[10px] txt-muted font-section">BULL / BEAR split</div>
            </div>
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">SCAN CADENCE</div>
              <div className="text-2xl font-mono-data font-bold text-[var(--text-primary)]">{scanInterval}m</div>
              {lastScan && (
                <div className="text-[10px] txt-muted font-mono-data truncate">
                  last {new Date(lastScan).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
          </div>

          {/* Scanner performance table */}
          {scanners.length > 0 && (
            <div>
              <div className="text-[10px] font-section txt-muted tracking-wider mb-2">SCANNER PERFORMANCE · 30 DAYS</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[var(--border)]">
                      {["Scanner", "Signals", "Win %", "Avg R:R", "Expectancy"].map((h) => (
                        <th key={h} className="text-left pb-1.5 pr-4 text-[10px] font-section txt-muted tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {scanners.map((sc) => {
                      const wrColor = sc.winRate >= 60 ? "txt-profit" : sc.winRate >= 45 ? "text-amber-400" : "txt-loss";
                      const exColor = (sc.expectancy ?? 0) > 0 ? "txt-profit" : "txt-loss";
                      return (
                        <tr key={sc.key} className="border-b border-[var(--border)]/40 hover:bg-[var(--bg-surface-2)]/50">
                          <td className="py-1.5 pr-4 text-[var(--text-primary)] font-medium">
                            <ScannerLink scanner={sc.key} testid={`scorecard-scanner-link-${sc.key}`}>{sc.label}</ScannerLink>
                          </td>
                          <td className="py-1.5 pr-4 font-mono-data txt-secondary">{sc.signals}</td>
                          <td className={`py-1.5 pr-4 font-mono-data ${wrColor}`}>
                            {sc.winRate != null ? `${sc.winRate.toFixed(0)}%` : "—"}
                          </td>
                          <td className="py-1.5 pr-4 font-mono-data txt-secondary">
                            {sc.rr != null ? `${sc.rr.toFixed(1)}x` : "—"}
                          </td>
                          <td className={`py-1.5 font-mono-data font-bold ${exColor}`}>
                            {sc.expectancy != null ? (sc.expectancy > 0 ? "+" : "") + sc.expectancy.toFixed(2) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

class SignalListBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) {
      return (
        <div className="panel p-6 txt-muted text-sm text-center">
          Could not render signals — {this.state.err.message || "unknown error"}.
          Refresh the page to retry.
        </div>
      );
    }
    return this.props.children;
  }
}

const PIPELINE_LABELS = {
  trap:              { label: "Trap Signal",      color: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
  momentum:          { label: "Momentum",         color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  commodity_gap:     { label: "Commodity Gap",    color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
  orb:               { label: "Range Breakout",   color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30" },
  vwap:              { label: "VWAP Reversion",   color: "bg-teal-500/20 text-teal-300 border-teal-500/30" },
  mean_reversion:    { label: "Mean Reversion",   color: "bg-rose-500/20 text-rose-300 border-rose-500/30" },
  breakout:          { label: "Breakout",         color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
  fvg_ob:            { label: "FVG / Order Block",color: "bg-violet-500/20 text-violet-300 border-violet-500/30" },
  liquidity_sweep:   { label: "Liq. Sweep",       color: "bg-pink-500/20 text-pink-300 border-pink-500/30" },
  orb_intraday:      { label: "ORB Intraday",     color: "bg-cyan-400/20 text-cyan-200 border-cyan-400/30" },
};

const INSTR_BADGES = {
  EQ:  { label: "EQ",      color: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30" },
  FUT: { label: "FUTURES", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  OPT: { label: "OPTIONS", color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
};

function ConfidenceBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 70 ? "bg-green-500" : pct >= 50 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] txt-muted font-section shrink-0">Confidence</span>
      <div className="flex-1 h-1.5 bg-[var(--bg-surface-2)] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono-data txt-secondary w-8 text-right">{pct}%</span>
    </div>
  );
}

function RiskBadge({ score }) {
  const label = score <= 33 ? "LOW" : score <= 66 ? "MED" : "HIGH";
  const color = score <= 33
    ? "bg-green-500/20 text-green-300 border-green-500/30"
    : score <= 66
    ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/30"
    : "bg-red-500/20 text-red-300 border-red-500/30";
  return (
    <span className={`text-[10px] font-section px-1.5 py-0.5 rounded border ${color}`}>
      Risk: {label}
    </span>
  );
}

function InstrBadge({ type }) {
  const cfg = INSTR_BADGES[type] || INSTR_BADGES.EQ;
  return (
    <span className={`text-[10px] font-section px-1.5 py-0.5 rounded border font-bold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// RU-DRIFTUI (DU-3): merge a /signals/executable result into a signal list (unchanged cards keep identity).
const EXEC_KEYS = ["executable", "not_executable_reason", "exec_drift_pct", "exec_tolerance_pct", "exec_ltp"];
// RU-SIGSTATUS (SS-7): `states` ({id: {execution, status}}) from the same poll merges too; an unchanged
// execution summary (same JSON) and status keep the card's identity.
// RU-SIGUI2 (SU2-2): `quotes` ({id: {option_quote_live, paper_note}}, the poll's `option_quotes`) merges too.
const QUOTE_KEYS = ["option_quote_live", "paper_note"];
export function mergeExecStatus(list, items, states, quotes) {
  if (!items && !states && !quotes) return list;
  let changed = false;
  const out = list.map((s) => {
    const id = s.id ?? s._id;
    const st = items ? items[id] : undefined;
    const ss = states ? states[id] : undefined;
    const sq = quotes ? quotes[id] : undefined;
    let next = s;
    if (st && !EXEC_KEYS.every((k) => s[k] === st[k])) next = { ...next, ...st };
    if (sq && !QUOTE_KEYS.every((k) => s[k] === sq[k])) {
      next = { ...next, ...Object.fromEntries(QUOTE_KEYS.map((k) => [k, sq[k]])) };
    }
    if (ss) {
      const exec = ss.execution === undefined ? s.execution : ss.execution;
      const status = ss.status === undefined || ss.status === null ? s.status : ss.status;
      if (JSON.stringify(exec) !== JSON.stringify(s.execution) || status !== s.status) {
        next = { ...next, execution: exec, status };
      }
    }
    if (next !== s) changed = true;
    return next;
  });
  return changed ? out : list;
}

// RU-SIGUI2 (SU2-4/SU2-5): an OPT signal whose underlying has no real option quote (simulated chain only).
// Absent (older backend, non-OPT) means unknown: Paper stays as before; resolve_fill is the final guard.
export const SIMULATED_OPTION_NOTE = "No real option quote — simulated chain only";
export function noRealOptionQuote(signal) {
  return String(signal?.instrument_type || "EQ").toUpperCase() === "OPT" && signal?.option_quote_live === false;
}
/** SU2-5: premium label of the Options-play line, via lib/priceLabel (as Dashboard's chainSourceLabel). */
export function optionPremiumLabel(signal) {
  if (typeof signal?.option_quote_live !== "boolean") return null;
  const src = signal.option_quote_live ? "live" : "mock";
  const lab = priceLabel({ ltp: 0, source: src, is_live: src === "live" });
  return lab === "SIMULATED" ? "simulated" : lab === "LIVE" ? "live" : null;
}
/** SU2-6: raw price move of the LTP against entry (%, 2 dp, own sign), whatever the signal's direction. */
export function ltpMovePct(ltp, entry) {
  const l = Number(ltp);
  const e = Number(entry);
  if (ltp == null || entry == null || !Number.isFinite(l) || !Number.isFinite(e) || e <= 0) return null;
  return Math.round((l - e) / e * 100 * 100) / 100;
}

// RU-SIGSTATUS (SS-9): a broker-searchable instrument name. `contract` is the expiry the live/preview
// policy resolved ({expiry: "YYYY-MM-DD", index, underlying}); without it a derivative copies the underlying only.
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
export function brokerSymbolText(signal, contract) {
  const sym = String(signal?.symbol || "").trim().toUpperCase();
  const itype = String(signal?.instrument_type || "EQ").toUpperCase();
  if (itype !== "OPT" && itype !== "FUT") return sym;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(contract?.expiry || ""));
  if (!m) return sym;                                     // contract unknown: never a guessed month
  const under = String(contract.underlying || sym).trim().toUpperCase();
  const mon = MONTHS[Number(m[2]) - 1];
  if (!mon) return sym;
  if (itype === "FUT") return `${under} ${mon} FUT`;
  const kind = String(signal.option_kind || "").toUpperCase();
  const strike = Number(signal.option_strike);
  if (!(kind === "CE" || kind === "PE") || !Number.isFinite(strike) || strike <= 0) return under;
  const day = contract.index ? `${Number(m[3])} ` : "";
  return `${under} ${day}${mon} ${String(strike)} ${kind}`;
}

// RU-SIGSTATUS (SS-4..SS-6): what this user already did with the signal.
export const SERVER_LOCKED_STATUSES = ["executing", "executed", "execution_unknown"];
const LIVE_LOCKING = ["PLACED", "FILLED", "UNKNOWN", "MAY_BE_PLACED"];

export function fmtIstTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit",
                                              hour12: false }).format(d);
  } catch {
    const ist = new Date(d.getTime() + 330 * 60 * 1000);
    return `${String(ist.getUTCHours()).padStart(2, "0")}:${String(ist.getUTCMinutes()).padStart(2, "0")}`;
  }
}

const statusLabel = (st) => String(st || "").replace(/_/g, " ");

// RU-SIGPAPER (SP-6/SP-7): this user's own paper Execute of the signal (the per-user paper claim), or null.
// Paper is once per user; the signal's status is the global live claim and never disables Paper.
export function paperExecOf(execution) {
  const p = execution && !execution.unavailable ? execution.paper_exec : null;
  return p && p.status ? p : null;
}
export function paperExecTitle(paperExec) {
  return `You already paper-traded this signal (${statusLabel(paperExec.status)}) — one paper Execute per signal`;
}
const brokerLabel = (b) => (b ? String(b).charAt(0).toUpperCase() + String(b).slice(1) : "Broker");

export function paperChipText(paper) {
  const n = Number(paper?.count) || 0;
  const t = fmtIstTime(paper?.last_at);
  return `Paper ✓ ${n} ${n === 1 ? "entry" : "entries"}${t ? ` · ${t} IST` : ""}`;
}

export function liveChipText(o) {
  const bad = o.status === "REJECTED" || o.status === "NOT_PLACED";
  const amber = o.status === "UNKNOWN" || o.status === "MAY_BE_PLACED";
  const parts = [`Live ${bad ? "✗" : "✓"}`, brokerLabel(o.broker), statusLabel(o.status)];
  if (o.qty != null) parts.push(String(o.qty));
  let text = parts.join(" ");
  if (o.avg_price != null) text += ` @ ${o.avg_price}`;
  const t = fmtIstTime(o.at);
  if (t) text += ` · ${t} IST`;
  if (amber) text += " · check broker";
  return text;
}

const LIVE_CHIP_CLASS = {
  FILLED: "bg-green-500/15 text-green-300 border-green-500/40",
  PLACED: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  UNKNOWN: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  MAY_BE_PLACED: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  REJECTED: "bg-red-500/15 text-red-300 border-red-500/40",
  NOT_PLACED: "bg-red-500/15 text-red-300 border-red-500/40",
};

export function liveLockOf(execution) {
  const live = execution?.live;
  if (!live || !live.locked) return null;
  const latest = live.latest || {};
  const status = live.lock_status || latest.status;
  if (!LIVE_LOCKING.includes(status)) return null;
  return { status, broker: live.lock_broker || latest.broker };
}

export function ExecChips({ execution }) {
  if (!execution) return null;
  if (execution.unavailable) {
    return (
      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="exec-chips">
        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/40"
              data-testid="chip-unavailable">
          Order status unavailable — check Positions
        </span>
      </div>
    );
  }
  const paper = execution.paper && Number(execution.paper.count) > 0 ? execution.paper : null;
  const latest = execution.live?.latest || null;
  if (!paper && !latest) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="exec-chips">
      {paper && (
        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded border bg-zinc-500/15 text-zinc-200 border-zinc-500/40"
              data-testid="chip-paper">
          {paperChipText(paper)}
        </span>
      )}
      {latest && (
        <span className={`text-[10px] font-mono-data px-1.5 py-0.5 rounded border ${LIVE_CHIP_CLASS[latest.status] || LIVE_CHIP_CLASS.MAY_BE_PLACED}`}
              data-testid="chip-live" data-status={latest.status}>
          {liveChipText(latest)}
        </span>
      )}
    </div>
  );
}
export const EXEC_POLL_MS = 15 * 1000;

// RU-PREVIEWGATE2 (PG2-8): may an order exist after this Execute error? Anything that is not a proven refusal
// is ambiguous: "PLACED:" / "MAY BE PLACED:", a 504, no response at all (network error, timeout, dropped
// connection) or any 5xx without a server detail (gateway/error page, PG2-14). A response carrying any other
// server detail (409, 422, 5xx with detail, ...) is a proven not-placed refusal.
export function executeOutcomeMayBePlaced(e) {
  const res = e?.response;
  if (!res) return true;
  const status = res.status;
  const detail = res.data && typeof res.data === "object" ? res.data.detail : undefined;
  if (typeof detail === "string" && /^(PLACED|MAY BE PLACED):/.test(detail)) return true;
  if (status === 504) return true;
  // PG2-14: any 5xx without a server detail (gateway/crash page, empty body) — the order may have gone out
  if (status >= 500 && status <= 599 && (detail === undefined || detail === null || detail === "")) return true;
  return false;
}

// RU-PREVIEWGATE2 (PG2-3/PG2-8): resolves true when the panel should close (success or an order may exist),
// false on a proven not-placed failure (the panel stays open).
export async function executeSignal(signalId, mode, qty, broker, previewToken, liveBroker) {
  const chosenBroker = broker || liveBroker;
  if (mode === "live" && !chosenBroker) {
    toast.error("No live broker connected. Connect one in Brokers page first.");
    return false;
  }
  try {
    const { data } = await api.post(`/signals/${signalId}/execute`, {
      mode,
      broker: mode === "live" ? chosenBroker : undefined,
      qty_override: qty,
      ...(previewToken ? { preview_token: previewToken } : {}),
    });
    const brokerLabel = mode === "live" ? ` via ${chosenBroker.toUpperCase()}` : "";
    const label = `${mode === "paper" ? "Paper" : "Live"} order${brokerLabel}: ${data.side}`;
    if (data.needs_attention) {
      // Filled under one lot: no stop/target bracket exists for it.
      toast.warning(
        `${label} ${data.symbol} filled under one lot (requested ${data.requested_qty}) — no stop/target; manage it at the broker`,
        { duration: 15000 }
      );
    } else if (data.partial_fill) {
      toast.warning(
        `${label} ${data.symbol} partially filled ${data.qty} of ${data.requested_qty} — bracket for ${data.qty}`,
        { duration: 10000 }
      );
    } else {
      toast.success(`${label} ${data.qty} × ${data.symbol}`);
    }
    return true;
  } catch (e) {
    const detail = e?.response?.data?.detail;
    const msg = typeof detail === "string" ? detail
              : Array.isArray(detail) ? detail.map((d) => d.msg || d.type).join(", ")
              : e?.message || "Execution failed";
    // Placed but not fully recorded, may be placed, or outcome unknown: not a plain failure — do not resend.
    if (executeOutcomeMayBePlaced(e)) {
      const warn = typeof detail === "string" && detail ? msg
                 : `Order status unknown (${msg}) — check Positions / the broker before trying again`;
      toast.warning(warn, { duration: 15000 });
      return true;   // RU-PREVIEWGATE2 (PG2-3): an order may exist — close the panel, never resend
    }
    toast.error(msg);
    return false;    // PG2-3: the confirm panel stays open
  }
}

// RU-SIGMARGIN (SM-1/SM-10): one line of a broker's required-margin answer (GET /signals/{id}/margin). The
// number is always the broker's own calculation; an unavailable answer shows its reason, never a figure.
export function marginText(data) {
  if (!data) return "";
  if (data.status !== "ok" || data.required_margin == null) {
    return `unavailable: ${data.reason || "no answer"}`;
  }
  const rupees = Number(data.required_margin).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const size = data.path === "exact_option_entry"
    ? `${data.lots || 1} lot (${data.qty} units)`
    : `${data.qty}`;
  const what = [data.tradingsymbol, data.product].filter(Boolean).join(" ");
  const when = fmtIstTime(data.checked_at);
  return `₹${rupees} for ${size}${what ? ` (${what})` : ""} · ${data.source || "broker margin calculator"}`
    + `${when ? `, ${when} IST` : ""}${data.note ? ` · ${data.note}` : ""}`;
}

// RU-DHANUI (DU-1): live option entries on these brokers go to the exact contract their preview resolved
// (preview-gated Live, token sent with Execute). Adding a broker is a one-line change here.
export const EXACT_ENTRY_BROKERS = new Set(["zerodha", "dhan", "upstox", "angelone"]);   // RU-UPSTOXOPT UO-A2-1; RU-ANGELNFO AN-21

export function SignalCard({ signal, onExecute, expectancy = {}, liveBrokers = [], defaultBroker = null,
                      watch = null, onWatch, onCancelWatch }) {
  const [expanded,      setExpanded]      = useState(false);
  const [confirming,    setConfirming]    = useState(null); // null | "paper" | "live"
  const [execQty,       setExecQty]       = useState(1);
  const [selectedBroker, setSelectedBroker] = useState(null); // overrides defaultBroker per-card
  const [executing,     setExecuting]     = useState(false);
  const [swipeDx,       setSwipeDx]       = useState(0);
  const [swiped,        setSwiped]        = useState(null);
  const touchStartX = useRef(null);
  const [watchForm,     setWatchForm]     = useState(false);
  const [watchQty,      setWatchQty]      = useState("");
  const [watchNote,     setWatchNote]     = useState("");
  const [watchBusy,     setWatchBusy]     = useState(false);

  const chosenBroker = selectedBroker || defaultBroker;
  // RU-SIGMARGIN (SM-10): per-broker required margin, fetched only when Check is pressed (never on render/poll)
  const [margins,       setMargins]       = useState({});   // broker -> {loading} | {data} | {error}

  // RU-G32B-2: a live Zerodha option entry goes to the exact contract its preview resolved
  // (LIMIT IOC from the Zerodha quote, 1 lot). The preview token is sent with Execute.
  // RU-PREVIEWGATE2 (PG2-1): fail closed — Live + Zerodha + OPT always needs the preview, whatever the
  // client believes about the switches; with them off the backend preview refuses (409) and Live stays disabled.
  // RU-DHANUI (DU-1/DU-2): the same gate for every broker in EXACT_ENTRY_BROKERS (Zerodha, Dhan, Upstox); the
  // preview is asked of the chosen broker. The explicit Zerodha term is kept (PG2-1 source pin).
  const exactBroker = String(chosenBroker || "").toLowerCase();
  const exactEntry = confirming === "live" && (signal.instrument_type || "EQ") === "OPT"
    && (exactBroker === "zerodha" || EXACT_ENTRY_BROKERS.has(exactBroker));
  const exactBrokerLabel = brokerLabel(exactBroker);
  const [livePreview,   setLivePreview]   = useState(null);  // null | {loading} | {data} | {error}
  const [previewRound,  setPreviewRound]  = useState(0);     // bumped to re-preview before the token expires
  const [, setExpiryTick] = useState(0);                     // re-render when a preview token expires (PG-1)

  useEffect(() => {
    if (!exactEntry) { setLivePreview(null); return undefined; }
    let alive = true;
    let timer = null;
    let expiry = null;
    // a re-preview keeps the shown preview until the new one arrives (CB2-27 compares them);
    // RU-DHANUI (DU-5): another broker's preview is dropped at once (never sent, never compared)
    const sameBroker = (old) => old?.data && old.broker === exactBroker;
    setLivePreview((old) => (sameBroker(old) ? { ...old, loading: true } : { loading: true }));
    api.post(`/signals/${signal.id}/live-preview`, null, { params: { broker: exactBroker } })
      .then(({ data }) => {
        if (!alive) return;
        // CB2-27: keep the previous preview so a changed limit or premium at risk is shown
        // RU-PREVIEWGATE (PG-1): receivedAt dates the token so an expired one is never sent
        setLivePreview((old) => ({ data, prevPreview: old?.data || null, receivedAt: Date.now(),
                                   broker: exactBroker }));
        // a preview token is single-use and short-lived (preview_ttl_s): refresh it before it expires
        const ttl = Number(data.preview_ttl_s) || 60;
        timer = setTimeout(() => setPreviewRound((n) => n + 1), Math.max(5, ttl - 10) * 1000);
        // if the refresh has not replaced it by then, re-render at expiry so Live disables
        expiry = setTimeout(() => setExpiryTick((n) => n + 1), ttl * 1000 + 50);
      })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        const msg = typeof d === "string" ? d : (d && d.message) || e?.message || "Preview failed";
        if (alive) setLivePreview({ error: msg });
      });
    return () => { alive = false; if (timer) clearTimeout(timer); if (expiry) clearTimeout(expiry); };
  }, [exactEntry, exactBroker, signal.id, previewRound]);

  // RU-PREVIEWGATE (PG-1): an exact entry is sent only with a usable, unexpired preview token. With no
  // preview yet, a failed preview, a refresh in flight or an expired token, Live stays disabled (the
  // backend would refuse it). Evaluated at render and again on confirm (a backgrounded tab's timers lag).
  function previewUsable() {
    if (!exactEntry) return true;
    const p = livePreview;
    if (!p?.data?.preview_token || p.loading || p.error) return false;
    if (p.broker !== exactBroker) return false;   // RU-DHANUI (DU-10): never another broker's token
    const ttl = Number(p.data.preview_ttl_s) || 60;
    return Date.now() < (p.receivedAt || 0) + ttl * 1000;
  }
  const exactReady = previewUsable();
  // RU-PREVIEWGATE2 (PG2-2): a refused preview labels the button with the refusal
  const previewRefused = exactEntry && !!livePreview?.error;

  // RU-DRIFTUI (DU-4): the backend's execution_check verdict (GET /signals and /signals/executable).
  // Absent (older backend) means executable, exactly as before; the execute endpoint stays the final guard.
  const blocked = signal.executable === false;
  const blockedReason = signal.not_executable_reason || "Signal is not executable at the current price";
  // An open confirm row closes when the signal turns non-executable (Paper/Live are then disabled).
  useEffect(() => { if (blocked) setConfirming(null); }, [blocked]);

  // RU-SIGSTATUS (SS-5): a live order of this user from this signal that is (or may be) at the broker locks
  // Live; the server's live claim (executing/executed/execution_unknown) refuses another Live Execute only
  // (RU-SIGPAPER SP-7/SP-8: a paper Execute is per user and never sets this status).
  const liveLock = liveLockOf(signal.execution);
  const serverLocked = SERVER_LOCKED_STATUSES.includes(signal.status);
  const serverLockReason = `Signal already ${statusLabel(signal.status)} — the server refuses another execute`;
  const liveLockTitle = liveLock
    ? `A live order from this signal is already ${statusLabel(liveLock.status)} at ${brokerLabel(liveLock.broker)} — add live size via the order ticket`
    : null;
  const liveDisabled = blocked || serverLocked || !!liveLock;
  // RU-SIGUI2 (SU2-4): no Paper where paper would price from the simulated chain (resolve_fill refuses it)
  const noOptQuote = noRealOptionQuote(signal);
  const noOptQuoteReason = signal.paper_note || SIMULATED_OPTION_NOTE;
  // RU-SIGPAPER (SP-7): Paper never follows the signal's (live) status; only this user's own paper Execute
  const paperExec = paperExecOf(signal.execution);
  const paperExecReason = paperExec ? paperExecTitle(paperExec) : null;
  const paperDisabled = blocked || !!paperExec || noOptQuote;
  // SU2-4: an open Paper confirm row closes when the quote source turns simulated (the Live row is untouched)
  useEffect(() => { if (noOptQuote) setConfirming((c) => (c === "paper" ? null : c)); }, [noOptQuote]);
  // SS-11: never while this card's own Execute is in flight (that Execute is what made the signal "executing");
  // a refused Execute keeps the panel open (PG2-3), a successful one closes it in confirmExecute.
  // RU-SIGPAPER (SP-7): the status lock closes only the Live row; this user's paper Execute closes the Paper row.
  useEffect(() => {
    if (executing) return;
    if (liveLock || serverLocked) setConfirming((c) => (c === "live" ? null : c));
  }, [!!liveLock, serverLocked]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (executing) return;
    if (paperExec) setConfirming((c) => (c === "paper" ? null : c));
  }, [!!paperExec]); // eslint-disable-line react-hooks/exhaustive-deps
  // RU-SIGSTATUS (SS-6): another paper entry from the same signal is confirmed first
  const paperCount = Number(signal.execution?.paper?.count) || 0;
  // RU-SIGMARGIN (SM-10): the quantity is the one this card's Execute would send (an exact option entry: 1 lot)
  const marginEligible = liveBrokers.length > 0 && !serverLocked;
  async function checkMargin(b) {
    const exact = (signal.instrument_type || "EQ") === "OPT" && EXACT_ENTRY_BROKERS.has(String(b).toLowerCase());
    const qty = exact ? 1 : (Number(execQty) || 1);
    setMargins((m) => ({ ...m, [b]: { loading: true } }));
    try {
      const { data } = await api.get(`/signals/${signal.id}/margin`, { params: { broker: b, qty } });
      setMargins((m) => ({ ...m, [b]: { data } }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d && d.message) || e?.message || "margin check failed";
      setMargins((m) => ({ ...m, [b]: { error: msg } }));
    }
  }

  function confirmPaperAgain() {
    if (paperCount <= 0) return true;
    const t = fmtIstTime(signal.execution?.paper?.last_at);
    return window.confirm(
      `You already have ${paperCount} paper ${paperCount === 1 ? "entry" : "entries"} from this signal`
      + `${t ? ` (last ${t} IST)` : ""}. Place another paper entry?`);
  }

  async function copySignal() {
    try {
      let text = signal.copy_text;
      if (!text) {
        const { data } = await api.get(`/manual-watches/copy-text/${signal.id}`);
        text = data.text;
      }
      await copyToClipboard(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  }

  // RU-SIGSTATUS (SS-9): copy only the instrument name (the exact previewed contract when one is shown)
  async function copySymbol() {
    // SS-15: a live preview's exact contract for OPT only; FUT always the server's resolved contract
    const pd = String(signal.instrument_type || "EQ").toUpperCase() === "OPT" ? livePreview?.data : null;
    const contract = pd?.expiry
      ? { expiry: pd.expiry, underlying: pd.underlying || signal.symbol,
          index: signal.copy_contract?.index ?? ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX", "BANKEX"]
            .includes(String(signal.symbol || "").toUpperCase()) }
      : signal.copy_contract;
    const text = brokerSymbolText(signal, contract);
    try {
      await copyToClipboard(text);
      toast.success(`Copied: ${text}`);
    } catch {
      toast.error("Copy failed");
    }
  }

  async function startWatch() {
    if (!onWatch) return;
    setWatchBusy(true);
    try {
      const ok = await onWatch(signal.id, watchQty, watchNote);
      if (ok) { setWatchForm(false); setWatchQty(""); setWatchNote(""); }
    } finally {
      setWatchBusy(false);
    }
  }

  async function stopWatch() {
    if (!onCancelWatch || !watch) return;
    setWatchBusy(true);
    try {
      await onCancelWatch(watch.id);
    } finally {
      setWatchBusy(false);
    }
  }

  function onTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
    setSwiped(null);
    setSwipeDx(0);
  }

  function onTouchMove(e) {
    if (touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    setSwipeDx(Math.max(-90, Math.min(90, dx)));
  }

  async function onTouchEnd() {
    const dx = swipeDx;
    touchStartX.current = null;
    setSwipeDx(0);
    if (dx > 60 && blocked) {
      // DU-4: no paper trade while the signal is not executable
      setSwiped(null);
      toast.error(`Not executable: ${blockedReason}`);
    } else if (dx > 60 && paperExec) {
      setSwiped(null);   // RU-SIGPAPER SP-7: one paper Execute per user; the live status lock never blocks paper
      toast.error(paperExecReason);
    } else if (dx > 60 && noOptQuote) {
      setSwiped(null);   // SU2-4: nothing is sent
      toast.error(noOptQuoteReason);
    } else if (dx > 60 && !confirmPaperAgain()) {
      setSwiped(null);   // SS-6: cancelled — nothing is sent
    } else if (dx > 60) {
      // Swipe right → instant paper trade
      setSwiped("paper");
      try {
        await onExecute(signal.id, "paper", 1);
      } catch {
        setSwiped(null);
      }
    } else if (dx < -60) {
      // Swipe left → dismiss card visually
      setSwiped("dismiss");
    } else {
      setSwiped(null);
    }
  }

  const isBull    = signal.direction === "BULLISH";
  const pip       = PIPELINE_LABELS[signal.pipeline] || { label: signal.pipeline, color: "bg-zinc-700 text-zinc-300 border-zinc-600" };
  const age       = _timeAgo(signal.created_at);
  const rr        = signal.risk_reward != null ? Number(signal.risk_reward) : null;
  const rrDisplay = rr != null && !isNaN(rr) ? `${rr.toFixed(1)}x` : "—";
  const instrType = signal.instrument_type || "EQ";
  const entry  = signal.entry_price  != null ? Number(signal.entry_price)  : null;
  const sl     = signal.stop_loss    != null ? Number(signal.stop_loss)    : null;
  const tgt    = signal.target_1     != null ? Number(signal.target_1)     : null;
  // RU-SIGUI2 (SU2-6): the LTP shows the raw price move; "Running" stays direction-adjusted (SU2-7)
  const ltpMove = ltpMovePct(signal.ltp, entry);
  const premiumLabel = instrType === "OPT" ? optionPremiumLabel(signal) : null;

  async function confirmExecute() {
    if (blocked) return; // RU-DRIFTUI DU-4: the verdict may have changed since the row opened
    if (executing || !previewUsable()) return;
    if (confirming === "live" && (serverLocked || liveLock)) return;   // RU-SIGSTATUS SS-5 (Live only, SP-7)
    if (confirming === "paper" && paperExec) return;                    // RU-SIGPAPER SP-7
    if (confirming === "paper" && noOptQuote) return;                    // RU-SIGUI2 SU2-4
    if (confirming === "paper" && !confirmPaperAgain()) return;          // SS-6: cancelled, row stays open
    setExecuting(true);
    try {
      const token = exactEntry ? livePreview?.data?.preview_token : undefined;
      let ok;
      try {
        ok = await onExecute(signal.id, confirming, exactEntry ? 1 : (Number(execQty) || 1),
                             confirming === "live" ? chosenBroker : undefined, token);
      } catch (err) {
        // RU-PREVIEWGATE2 (PG2-11): a throw is a failure — panel stays open, no unhandled rejection
        ok = false;
        toast.error(err?.message || "Execution failed");
      }
      // RU-PREVIEWGATE2 (PG2-3): close only on success; a failed exact entry asks for a fresh preview
      if (ok !== false) setConfirming(null);
      else if (exactEntry) setPreviewRound((n) => n + 1);
    } finally {
      setExecuting(false);
    }
  }

  if (swiped === "dismiss") return null;

  // SS-16: no "Paper trade" hint where the swipe would place nothing (blocked or server-locked)
  // RU-SIGPAPER SP-7: a paper-locked card (this user's own paper Execute) shows no hint; the live status lock does not
  const swipeHint = swipeDx > 20 ? (blocked || paperExec || noOptQuote ? null : "paper") : swipeDx < -20 ? "dismiss" : null;

  return (
    <div
      className={`border rounded-sm transition-all relative overflow-hidden select-none ${
        isBull ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"
      } ${swiped === "paper" ? "opacity-50" : ""}`}
      style={{ transform: `translateX(${swipeDx}px)`, transition: swipeDx === 0 ? "transform 0.25s ease" : "none" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Swipe hint overlays — mobile only */}
      {swipeHint === "paper" && (
        <div className="absolute inset-0 bg-green-500/20 flex items-center justify-start pl-4 pointer-events-none z-10">
          <span className="text-green-300 text-sm font-section">Paper trade ↗</span>
        </div>
      )}
      {swipeHint === "dismiss" && (
        <div className="absolute inset-0 bg-zinc-500/20 flex items-center justify-end pr-4 pointer-events-none z-10">
          <span className="text-zinc-400 text-sm font-section">Dismiss ✕</span>
        </div>
      )}
      {/* Header row */}
      <div className="p-3 sm:p-4 flex items-start gap-3">
        <div className={`mt-0.5 w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${
          isBull ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
        }`}>
          {isBull ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display text-base text-[var(--text-primary)]">{signal.symbol}</span>
            <span className={`text-[10px] font-section px-1.5 py-0.5 rounded border ${
              isBull ? "bg-green-500/20 text-green-300 border-green-500/30"
                     : "bg-red-500/20 text-red-300 border-red-500/30"
            }`}>
              {signal.direction}
            </span>
            <InstrBadge type={instrType} />
            {/* RU-SCANPERF SP-9: the scanner name opens that scanner's performance page */}
            <ScannerLink scanner={signal.pipeline} testid={`signal-scanner-link-${signal.id}`}
                         className={`text-[10px] font-section px-1.5 py-0.5 rounded border ${pip.color}`}>
              {pip.label}
            </ScannerLink>
            <RiskBadge score={signal.risk_score} />
            {signal.vote_count > 1 && (
              <span className="text-[10px] font-section px-1.5 py-0.5 rounded border bg-indigo-500/20 text-indigo-300 border-indigo-500/30" title={`${signal.sources?.join(", ")}`}>
                {signal.vote_count} scanners
              </span>
            )}
            <span className="text-[10px] txt-muted ml-auto shrink-0">{age}</span>
          </div>

          {/* RU-SIGSTATUS (SS-4): what this user already did with the signal */}
          <ExecChips execution={signal.execution} />

          {/* Options play suggestion */}
          {signal.option_strike && signal.option_kind && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs">
              <span className="txt-muted">Options play:</span>
              <span className="font-mono-data text-purple-300 font-bold">
                Buy {signal.symbol} {signal.option_strike} {signal.option_kind}
              </span>
              <span className="txt-muted">(ATM {isBull ? "Call" : "Put"})</span>
              {premiumLabel && (
                <span
                  className={`text-[9px] font-section tracking-wider px-1 rounded border whitespace-nowrap ${
                    premiumLabel === "simulated" ? "text-amber-300 border-amber-500/40" : "txt-muted border-[var(--border)]"}`}
                  title={premiumLabel === "simulated" ? noOptQuoteReason : undefined}
                  data-testid={`signal-premium-label-${signal.id}`}
                >
                  premium: {premiumLabel}
                </span>
              )}
            </div>
          )}

          {/* Futures trade type */}
          {instrType === "FUT" && signal.trap_data?.trade_type && (
            <div className="mt-1 text-[10px] txt-muted">
              Recommended action: <span className="text-blue-300 font-section font-bold">{signal.trap_data.trade_type.replace("_", " ")}</span>
            </div>
          )}

          <div className="mt-2">
            <ConfidenceBar value={signal.confidence_score} />
          </div>

          {/* Live win rate badge from QuantEngine */}
          {expectancy[signal.pipeline] && (
            <div className="mt-1.5 flex items-center gap-3 text-[11px]">
              {(() => {
                const exp = expectancy[signal.pipeline];
                const wr = exp.win_rate_pct;
                const wrColor = wr >= 60 ? "text-green-400" : wr >= 45 ? "text-yellow-400" : "text-red-400";
                return (
                  <>
                    <span className="txt-muted">30d live:</span>
                    <span className={`font-mono font-medium ${wrColor}`}>{wr.toFixed(1)}% win</span>
                    <span className="txt-muted">·</span>
                    <span className="txt-secondary">{exp.trade_count} trades</span>
                    {exp.expectancy_per_trade !== 0 && (
                      <>
                        <span className="txt-muted">·</span>
                        <span className={exp.expectancy_per_trade > 0 ? "text-green-400" : "text-red-400"}>
                          ₹{exp.expectancy_per_trade > 0 ? "+" : ""}{exp.expectancy_per_trade.toFixed(0)}/trade
                        </span>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Key levels + LTP */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <div>
              <div className="overline txt-muted">{instrType === "OPT" ? "Underlying" : "Entry"}</div>
              <div className="font-mono-data text-[var(--text-primary)]">{entry != null ? entry.toFixed(2) : "—"}</div>
              {instrType === "OPT" && <div className="text-[9px] txt-muted mt-0.5">stock price</div>}
            </div>
            <div>
              <div className="overline txt-muted">LTP</div>
              {signal.ltp != null ? (
                <div className={`font-mono-data font-semibold ${ltpMove > 0 ? "txt-profit" : ltpMove < 0 ? "txt-loss" : "text-[var(--text-primary)]"}`}
                     data-testid={`signal-ltp-${signal.id}`}>
                  {Number(signal.ltp).toFixed(2)}
                  {ltpTag(signal) && (
                    <span className="text-[9px] ml-1 txt-muted font-section tracking-wider" data-testid={`signal-ltp-label-${signal.id}`}>
                      {ltpTag(signal)}
                    </span>
                  )}
                  {ltpMove != null && (
                    <span className="text-[9px] ml-1" data-testid={`signal-ltp-move-${signal.id}`}>
                      ({ltpMove > 0 ? "+" : ""}{ltpMove.toFixed(2)}% vs entry)
                    </span>
                  )}
                </div>
              ) : (
                <div className="font-mono-data txt-muted">—</div>
              )}
            </div>
            <div>
              <div className="overline txt-muted">Stop Loss</div>
              <div className="font-mono-data txt-loss">{sl != null ? sl.toFixed(2) : "—"}</div>
            </div>
            <div>
              <div className="overline txt-muted">Target</div>
              <div className="font-mono-data txt-profit">{tgt != null ? tgt.toFixed(2) : "—"}</div>
              {tgt != null && entry != null && entry > 0 && (
                <div className="text-[9px] txt-profit mt-0.5">
                  {isBull
                    ? `+${((tgt - entry) / entry * 100).toFixed(1)}%`
                    : `+${((entry - tgt) / entry * 100).toFixed(1)}%`}
                </div>
              )}
            </div>
            <div>
              <div className="overline txt-muted">Running</div>
              <div className={`font-mono-data text-sm font-semibold ${signal.unrealized_pct > 0 ? "txt-profit" : signal.unrealized_pct < 0 ? "txt-loss" : "txt-muted"}`}
                   data-testid={`signal-running-${signal.id}`}>
                {signal.unrealized_pct != null
                  ? `${signal.unrealized_pct > 0 ? "+" : ""}${signal.unrealized_pct}%`
                  : "—"}
              </div>
              {instrType === "OPT" && <div className="text-[9px] txt-muted mt-0.5">underlying move</div>}
            </div>
            <div>
              <div className="overline txt-muted">R:R</div>
              <div className="font-mono-data text-[var(--text-primary)]">{rrDisplay}</div>
            </div>
          </div>
        </div>
      </div>

      {/* RU-SIGMARGIN (SM-10): the broker's own required margin for the Live Execute order, on demand */}
      {marginEligible && (
        <div className="px-4 pb-2 text-xs" data-testid={`margin-row-${signal.id}`}>
          <div className="overline txt-muted">Required margin (live)</div>
          {liveBrokers.map((b) => {
            const m = margins[b];
            return (
              <div key={b} className="flex items-center gap-2 mt-0.5" data-testid={`margin-${b}`}>
                <span className="txt-secondary">{brokerLabel(b)}:</span>
                <span className={`font-mono-data ${m?.error || (m?.data && m.data.status !== "ok") ? "txt-muted" : "text-[var(--text-primary)]"}`}
                      data-testid={`margin-text-${b}`}>
                  {m?.loading ? "checking…"
                    : m?.data ? marginText(m.data)
                      : m?.error ? `unavailable: ${m.error}` : "—"}
                </span>
                <button type="button" onClick={() => checkMargin(b)} disabled={!!m?.loading}
                        className="text-[10px] underline txt-muted hover:text-[var(--text-primary)] disabled:opacity-50"
                        title="Ask the broker's margin calculator about the order Live Execute would place (nothing is placed)"
                        data-testid={`margin-check-${b}`}>
                  Check
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 pb-2 flex items-center gap-1 text-xs txt-muted hover:text-[var(--text-primary)] transition-colors"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? "Hide details" : "Show rationale + backtest"}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[var(--border)] pt-4 space-y-4">
          {signal.rationale && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Brain className="w-3.5 h-3.5 txt-secondary" />
                <span className="overline txt-secondary">AI Rationale</span>
              </div>
              <p className="text-sm txt-secondary leading-relaxed whitespace-pre-line">{signal.rationale}</p>
            </div>
          )}

          {signal.backtest && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <BarChart2 className="w-3.5 h-3.5 txt-secondary" />
                <span className="overline txt-secondary">Backtest (60 days)</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {[
                  ["Trades",       signal.backtest.total_trades],
                  ["Win Rate",     `${signal.backtest.win_rate_pct?.toFixed(1)}%`],
                  ["Sharpe",       signal.backtest.sharpe?.toFixed(2)],
                  ["Max DD",       `${signal.backtest.max_drawdown_pct?.toFixed(1)}%`],
                  ["Profit Factor",signal.backtest.profit_factor?.toFixed(2)],
                  ["Return",       `${signal.backtest.total_return_pct?.toFixed(1)}%`],
                ].map(([label, val]) => (
                  <div key={label} className="bg-[var(--bg-surface-2)] rounded-sm p-2">
                    <div className="overline txt-muted">{label}</div>
                    <div className="font-mono-data text-[var(--text-primary)]">{val ?? "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {signal.gap_data && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Flame className="w-3.5 h-3.5 text-purple-400" />
                <span className="overline txt-secondary">Gap Pattern</span>
                <span className={`text-xs font-section px-1.5 py-0.5 rounded ${
                  signal.gap_data.gap_direction === "gap_up" ? "bg-red-500/20 text-red-300" : "bg-green-500/20 text-green-300"
                }`}>
                  {signal.gap_data.gap_direction === "gap_up" ? "Gap Up" : "Gap Down"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-[var(--bg-surface-2)] rounded-sm p-2">
                  <div className="overline txt-muted">Gap Size</div>
                  <div className="font-mono-data text-[var(--text-primary)]">
                    {signal.gap_data.gap_pts?.toFixed(1)} pts ({signal.gap_data.gap_pct?.toFixed(2)}%)
                  </div>
                </div>
                <div className="bg-[var(--bg-surface-2)] rounded-sm p-2">
                  <div className="overline txt-muted">Fill Rate</div>
                  <div className="font-mono-data txt-profit">
                    {((signal.gap_data.fill_rate || 0) * 100).toFixed(0)}%
                    <span className="txt-muted ml-1">of {signal.gap_data.sample_size}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {signal.trap_data && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />
                <span className="overline txt-secondary">Trap Score</span>
                <span className="font-mono-data text-orange-300 text-xs">
                  {(signal.trap_data.overall_trap_score * 100).toFixed(0)}%
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {signal.trap_data.top_call_traps?.slice(0, 2).map((t) => (
                  <div key={t.strike} className="bg-red-500/10 border border-red-500/20 rounded-sm p-2">
                    <span className="overline text-red-400">CE Trap {t.strike}</span>
                    <div className="font-mono-data txt-secondary">
                      OI {(t.ce_oi / 100000).toFixed(1)}L · {(t.ce_trap * 100).toFixed(0)}%
                    </div>
                  </div>
                ))}
                {signal.trap_data.top_put_traps?.slice(0, 2).map((t) => (
                  <div key={t.strike} className="bg-green-500/10 border border-green-500/20 rounded-sm p-2">
                    <span className="overline text-green-400">PE Trap {t.strike}</span>
                    <div className="font-mono-data txt-secondary">
                      OI {(t.pe_oi / 100000).toFixed(1)}L · {(t.pe_trap * 100).toFixed(0)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual trade — copy to broker app + level alerts (RU-MCA). Never places an order. */}
      <div className="px-3 sm:px-4 pb-2">
        {watchForm && !watch ? (
          <div className="flex items-center gap-2 flex-wrap border border-[var(--border)] bg-[var(--bg-surface)] p-2 rounded-sm">
            <span className="text-[10px] font-section txt-secondary shrink-0">I placed this manually — watch SL / TG / exit</span>
            <Input
              type="number"
              min="1"
              placeholder="Qty (optional)"
              value={watchQty}
              onChange={(e) => setWatchQty(e.target.value)}
              className="h-7 w-28 rounded-none text-xs font-mono-data bg-[var(--bg-page)] border-[var(--border)] px-2"
            />
            <Input
              type="text"
              maxLength={200}
              placeholder="Note (optional)"
              value={watchNote}
              onChange={(e) => setWatchNote(e.target.value)}
              className="h-7 flex-1 min-w-[8rem] rounded-none text-xs bg-[var(--bg-page)] border-[var(--border)] px-2"
              onKeyDown={(e) => e.key === "Enter" && startWatch()}
            />
            <Button
              size="sm"
              onClick={startWatch}
              disabled={watchBusy}
              className="h-7 rounded-none text-xs bg-[var(--bg-surface-2)] hover:bg-[var(--border)] text-[var(--text-primary)]"
            >
              {watchBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
              Watch
            </Button>
            <button onClick={() => setWatchForm(false)} className="p-1 txt-muted hover:text-[var(--text-primary)]">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={copySignal}
              title="Copy instrument, side, entry, SL, targets, exit and validity for your broker app"
              className="flex-1 h-7 rounded-none text-xs border-[var(--border)] txt-secondary hover:text-[var(--text-primary)]"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={copySymbol}
              title="Copy only the instrument name to search in your broker app"
              data-testid="copy-symbol"
              className="h-7 rounded-none text-xs border-[var(--border)] txt-secondary hover:text-[var(--text-primary)] px-2"
            >
              Copy symbol
            </Button>
            {watch ? (
              <Button
                size="sm"
                variant="outline"
                onClick={stopWatch}
                disabled={watchBusy}
                title="Alerts are on for this trade's SL, targets and exit time. Click to cancel."
                className="flex-1 h-7 rounded-none text-xs border-amber-500/40 text-amber-300 hover:text-[var(--text-primary)]"
              >
                {watchBusy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Watching ✓ (cancel)
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setWatchForm(true)}
                title="I placed this manually — alert me once at SL, each target and the exit time"
                className="flex-1 h-7 rounded-none text-xs border-[var(--border)] txt-secondary hover:text-[var(--text-primary)]"
              >
                🔔 Alert me
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Action buttons — two-step with qty */}
      <div className="px-3 sm:px-4 pb-3 sm:pb-4">
        {blocked && (
          <div className="mb-2 text-[11px] font-mono-data text-amber-300 border border-amber-500/30 bg-amber-500/5 px-2 py-1 rounded-sm"
               data-testid="exec-blocked">
            Not executable: {blockedReason}
          </div>
        )}
        {confirming ? (
          <div className="space-y-2 border border-[var(--border)] bg-[var(--bg-surface)] p-2 rounded-sm">
            {/* Broker selector — live only */}
            {confirming === "live" && liveBrokers.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-section text-amber-400 shrink-0">BROKER</span>
                <div className="flex gap-1 flex-wrap">
                  {liveBrokers.map((b) => (
                    <button
                      key={b}
                      onClick={() => setSelectedBroker(b)}
                      className={`text-[10px] font-section px-2 py-0.5 rounded-sm border transition-colors ${
                        (selectedBroker || defaultBroker) === b
                          ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                          : "border-[var(--border)] txt-muted hover:text-[var(--text-primary)]"
                      }`}
                    >
                      {b.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* RU-G32B-2: exact contract preview (live Zerodha / Dhan option entry, RU-DHANUI) */}
            {exactEntry && (
              <div className="text-[11px] font-mono-data space-y-0.5 border border-amber-500/30 bg-amber-500/5 p-2 rounded-sm"
                   data-testid="live-entry-preview">
                {livePreview?.loading && !livePreview?.data && (
                  <div className="txt-muted">Resolving the exact contract at {exactBrokerLabel}…</div>
                )}
                {livePreview?.loading && livePreview?.data && <div className="txt-muted">Refreshing the preview…</div>}
                {livePreview?.error && (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="dx-preview-error">
                    <span className="text-red-400">{livePreview.error}</span>
                    <button
                      type="button"
                      onClick={() => setPreviewRound((n) => n + 1)}
                      className="text-[10px] font-section px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 hover:text-[var(--text-primary)]"
                      data-testid="dx-retry-preview"
                    >
                      Retry preview
                    </button>
                  </div>
                )}
                {livePreview?.data && (() => {
                  const p = livePreview.data;
                  const prev = livePreview.prevPreview;
                  const limitChanged = prev && Number(prev.limit_price) !== Number(p.limit_price);
                  const riskChanged = prev && Number(prev.max_premium_at_risk) !== Number(p.max_premium_at_risk);
                  return (
                    <>
                      <div><span className="txt-muted">Contract </span>{p.exchange}:{p.tradingsymbol}</div>
                      <div><span className="txt-muted">Expiry </span>{p.expiry}</div>
                      <div><span className="txt-muted">Quantity </span>{p.lots} lot × {p.lot_size} = {p.units}</div>
                      <div>
                        <span className="txt-muted">Order </span>BUY LIMIT{" "}
                        <span className={limitChanged ? "bg-amber-500/30 text-amber-200 px-1" : ""} data-testid="dx-limit">
                          ₹{p.limit_price}
                        </span>
                        {limitChanged && <span className="text-amber-300"> (changed from ₹{prev.limit_price})</span>}
                        {" "}· {p.validity} · {p.product}
                      </div>
                      <div data-testid="dx-quote-source">
                        <span className="txt-muted">{p.quote_source === "dhan_fallback" ? "Dhan quote (fallback: Zerodha quote refused) "
                          : p.quote_source === "dhan" ? "Dhan quote " : p.quote_source === "upstox" ? "Upstox quote "
                          : p.quote_source === "angelone" ? "AngelOne quote " : "Zerodha quote "}</span>
                        LTP ₹{p.quote?.ltp} (bid {p.quote?.bid} / ask {p.quote?.ask})
                      </div>
                      <div className={`text-amber-300 ${riskChanged ? "bg-amber-500/30 px-1" : ""}`} data-testid="dx-risk">
                        Max premium at risk ₹{Number(p.max_premium_at_risk).toLocaleString("en-IN")}
                        {riskChanged && ` (changed from ₹${Number(prev.max_premium_at_risk).toLocaleString("en-IN")})`}
                      </div>
                      {/* RU-DHANUI (DU-6): the Dhan margin check (premium vs the cap; RU-FUNDSALL: available = cash + collateral) */}
                      {p.broker === "dhan" && p.margin && (
                        <div data-testid="dx-margin">
                          <span className="txt-muted">Margin check </span>
                          premium ₹{Number(p.margin.required).toLocaleString("en-IN")} ≤ cap ₹{Number(p.margin.cap).toLocaleString("en-IN")}
                          {" "}({Math.round(Number(p.margin.share) * 100)}% of available margin (cash + collateral) ₹{Number(p.margin.available).toLocaleString("en-IN")})
                        </div>
                      )}
                      {/* RU-ZCOLL (ZC-7): premium partly funded from pledged collateral → DPC warning (non-blocking) */}
                      {p.margin?.collateral_warning && (
                        <div className="text-amber-300" data-testid="dx-collateral-warning">{p.margin.collateral_warning}</div>
                      )}
                      {p.exit_note && <div className="text-amber-300" data-testid="dx-exit-note">{p.exit_note}</div>}
                      <div className="txt-muted">
                        Signal premium (simulated, not used): {p.signal_premium_simulated != null ? `₹${p.signal_premium_simulated}` : "—"}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {/* Qty + confirm row */}
            <div className="flex items-center gap-2">
              <span className="text-xs txt-secondary shrink-0">{exactEntry ? "Lots:" : "Qty:"}</span>
              <Input
                type="number"
                min="1"
                disabled={exactEntry}
                value={exactEntry ? 1 : execQty}
                onChange={(e) => setExecQty(e.target.value)}
                className="h-7 w-20 rounded-none text-xs font-mono-data bg-[var(--bg-page)] border-[var(--border)] px-2"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && confirmExecute()}
              />
              <span className="text-xs txt-muted shrink-0">{instrType}</span>
              {confirming === "live" && chosenBroker && (
                <span className="text-[10px] font-section text-amber-400 shrink-0">→ {chosenBroker.toUpperCase()}</span>
              )}
              <Button
                size="sm"
                onClick={confirmExecute}
                disabled={executing || !exactReady}
                title={previewRefused ? livePreview.error : exactReady ? undefined : `Waiting for a valid ${exactBrokerLabel} preview`}
                data-testid="confirm-execute"
                className={`h-7 rounded-none text-xs flex-1 ${
                  confirming === "paper" || previewRefused
                    ? "bg-[var(--bg-surface-2)] hover:bg-[var(--border)] text-[var(--text-primary)]"
                    : isBull ? "bg-green-600 hover:bg-green-500 text-white" : "bg-red-600 hover:bg-red-500 text-white"
                }`}
              >
                {executing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                {confirming === "paper" ? "Paper" : previewRefused ? "Live blocked" : "Live"}
              </Button>
              <button
                onClick={() => setConfirming(null)}
                className="p-1 txt-muted hover:text-[var(--text-primary)]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setExecQty(1); setConfirming("paper"); }}
              disabled={paperDisabled}
              title={blocked ? blockedReason : paperExec ? paperExecReason : noOptQuote ? noOptQuoteReason
                     : paperCount > 0 ? `You already have ${paperCount} paper ${paperCount === 1 ? "entry" : "entries"} from this signal` : undefined}
              data-testid="paper-btn"
              className="flex-1 rounded-none border-[var(--border)] txt-secondary hover:text-[var(--text-primary)]"
            >
              <Play className="w-3.5 h-3.5 mr-1.5" />
              {paperExec ? "Paper ✓" : paperCount > 0 ? "Paper +1" : "Paper"}
            </Button>
            <Button
              size="sm"
              onClick={() => { setExecQty(1); setConfirming("live"); }}
              disabled={liveDisabled}
              title={blocked ? blockedReason : liveLock ? liveLockTitle : serverLocked ? serverLockReason : undefined}
              data-testid="live-btn"
              className={`flex-1 rounded-none ${
                liveLock ? "bg-[var(--bg-surface-2)] text-[var(--text-primary)]"
                : isBull ? "bg-green-600 hover:bg-green-500 text-white" : "bg-red-600 hover:bg-red-500 text-white"
              }`}
            >
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              {liveLock ? "Live ✓ placed" : "Live"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function _timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

const PIPELINE_LABELS_H = {
  trap:             "Trap",
  momentum:         "Momentum",
  commodity_gap:    "Gap",
  orb:              "ORB",
  vwap:             "VWAP",
  mean_reversion:   "Mean Rev",
  breakout:         "Breakout",
  fvg_ob:           "FVG/OB",
  liquidity_sweep:  "Liq. Sweep",
  orb_intraday:     "ORB Intra",
};

export function HistorySignalRow({ signal }) {
  const isBull = signal.direction === "BULLISH";
  const exec   = signal.execution || {};
  const pnl    = exec.execution_pnl;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="border border-[var(--border)] bg-[var(--bg-surface)] px-4 py-3 flex items-center gap-4 flex-wrap">
      {/* Direction chip */}
      <span className={`w-2 h-2 rounded-full shrink-0 ${isBull ? "bg-emerald-500" : "bg-red-500"}`} />

      {/* Symbol + pipeline */}
      <div className="min-w-[120px]">
        <div className="font-section text-sm">{signal.symbol}</div>
        <div className="text-xs txt-muted">
          <ScannerLink scanner={signal.pipeline} testid={`history-scanner-link-${signal.id}`}>
            {PIPELINE_LABELS_H[signal.pipeline] || signal.pipeline}
          </ScannerLink>
        </div>
      </div>

      {/* Direction */}
      <div className="flex items-center gap-1 text-xs min-w-[70px]">
        {isBull
          ? <TrendingUp className="w-3.5 h-3.5 txt-profit" />
          : <TrendingDown className="w-3.5 h-3.5 txt-loss" />}
        <span className={isBull ? "txt-profit" : "txt-loss"}>{signal.direction}</span>
      </div>

      {/* Confidence */}
      <div className="text-xs txt-muted min-w-[60px]">
        Conf {((signal.confidence_score || 0) * 100).toFixed(0)}%
      </div>

      {/* Entry / SL / Target / Exit / LTP */}
      <div className="text-xs font-mono-data txt-secondary min-w-[200px] space-y-0.5">
        <div className="space-x-2">
          <span>E ₹{signal.entry_price}</span>
          <span>SL ₹{signal.stop_loss}</span>
          <span>T ₹{signal.target_1}</span>
        </div>
        <div className="flex gap-3">
          {exec.exit_price != null && (
            <span className="txt-secondary">Exit ₹{Number(exec.exit_price).toFixed(2)}</span>
          )}
          {signal.ltp != null && (
            <span className="txt-muted">
              LTP ₹{Number(signal.ltp).toFixed(2)}
              {ltpTag(signal) && <span className="text-[9px] ml-1 font-section tracking-wider">{ltpTag(signal)}</span>}
            </span>
          )}
        </div>
      </div>

      {/* Status + price outcome */}
      <div className="text-xs space-y-0.5">
        <span className={`px-1.5 py-0.5 font-section ${
          signal.status === "target_hit" ? "bg-emerald-900/40 txt-profit"
          : signal.status === "stop_hit" ? "bg-red-900/40 txt-loss"
          : signal.status === "expired" || signal.status === "superseded" || signal.status === "suppressed"
            ? "bg-zinc-700/50 txt-muted"
            : "bg-zinc-600/50 txt-secondary"
        }`}>
          {(signal.status || "").replace("_", " ").toUpperCase()}
        </span>
        {signal.outcome && signal.outcome !== signal.status && (
          <div className="txt-muted">price: {String(signal.outcome).replace("_", " ")}</div>
        )}
        {(signal.mfe_pct != null || signal.mae_pct != null) && (
          <div className="font-mono-data txt-muted">
            MFE +{Number(signal.mfe_pct || 0).toFixed(2)}% · MAE -{Number(signal.mae_pct || 0).toFixed(2)}%
            {signal.t2_hit ? " · T2" : ""}
          </div>
        )}
      </div>

      {/* Created at */}
      <div className="text-xs txt-muted">{fmtDate(signal.created_at)}</div>

      {/* Execution outcome */}
      <div className="ml-auto text-right text-xs">
        {exec.executed ? (
          <div>
            <div className="font-section txt-secondary">{exec.mode?.toUpperCase()} executed</div>
            {pnl != null ? (
              <div className={`font-mono-data font-semibold ${pnl > 0 ? "txt-profit" : pnl < 0 ? "txt-loss" : "txt-muted"}`}>
                {pnl > 0 ? "+" : ""}₹{Math.abs(pnl).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            ) : (
              <div className="txt-muted">P&L pending</div>
            )}
          </div>
        ) : (
          <div className="txt-muted">Not executed</div>
        )}
      </div>
    </div>
  );
}


export default function SignalFeed() {
  const [signals,      setSignals]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [scanning,     setScanning]     = useState(false);
  const [filter,       setFilter]       = useState("all");   // signal_group filter
  const [pipeFilter,   setPipeFilter]   = useState("all");   // individual pipeline filter
  const [direction,    setDirection]    = useState("all");
  const [instrFilter,  setInstrFilter]  = useState("all");
  const [total,        setTotal]        = useState(0);
  const [liveBroker,   setLiveBroker]   = useState(null);
  const [liveBrokers,  setLiveBrokers]  = useState([]); // all connected live brokers
  // RU-PREVIEWGATE2 (PG2-1): the exact-entry gate no longer depends on a client-side switch fetch
  // (it failed open); every live Zerodha OPT confirm asks the backend preview, which refuses when off.
  const [scanInterval, setScanInterval] = useState(30);
  const [isAdmin,      setIsAdmin]      = useState(false);
  const [view,          setView]          = useState("active"); // "active" | "history" | "watchlist"
  const [history,       setHistory]       = useState([]);
  const [histTotal,     setHistTotal]     = useState(0);
  const [histLoading,   setHistLoading]   = useState(false);
  const [watchSymbols,  setWatchSymbols]  = useState([]); // symbols with include_in_signals=true
  const [watchSignals,  setWatchSignals]  = useState([]);
  const [watchLoading,  setWatchLoading]  = useState(false);
  const [expectancy,    setExpectancy]    = useState({}); // keyed by scanner pipeline
  const [tierLimit,     setTierLimit]     = useState(false);
  const [lastScan,      setLastScan]      = useState(null);
  const [watches,       setWatches]       = useState({}); // signal_id -> active manual watch

  const fetchWatches = useCallback(async () => {
    try {
      const { data } = await api.get("/manual-watches", { params: { status: "active" } });
      const map = {};
      (data.items || []).forEach((w) => { map[w.signal_id] = w; });
      setWatches(map);
    } catch {
      console.warn("Failed to load manual-trade watches");
    }
  }, []);

  useEffect(() => {
    fetchWatches();
    const id = setInterval(fetchWatches, 60 * 1000); // a watch the watcher completed drops off
    return () => clearInterval(id);
  }, [fetchWatches]);

  function _errMsg(e, fallback) {
    const detail = e?.response?.data?.detail;
    return typeof detail === "string" ? detail
         : Array.isArray(detail) ? detail.map((d) => d.msg || d.type).join(", ")
         : e?.message || fallback;
  }

  async function handleWatch(signalId, qty, note) {
    try {
      const body = { signal_id: signalId };
      if (qty !== "" && qty != null) body.qty = Number(qty);
      if (note && note.trim()) body.note = note.trim();
      const { data } = await api.post("/manual-watches", body);
      setWatches((m) => ({ ...m, [signalId]: data }));
      toast.success("Watching — you get one alert each at SL, target(s) and exit time");
      return true;
    } catch (e) {
      toast.error(_errMsg(e, "Could not start the watch"));
      fetchWatches();
      return false;
    }
  }

  async function handleCancelWatch(watchId) {
    try {
      await api.delete(`/manual-watches/${watchId}`);
      toast.success("Watch cancelled");
    } catch (e) {
      toast.error(_errMsg(e, "Could not cancel the watch"));
    }
    fetchWatches();
  }

  const fetchSignals = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filter !== "all")      params.signal_group    = filter;
      if (pipeFilter !== "all")  params.pipeline        = pipeFilter;
      if (direction !== "all")   params.direction       = direction;
      if (instrFilter !== "all") params.instrument_type = instrFilter;
      const { data } = await api.get("/signals", { params });
      if (data.tier_limit) {
        setTierLimit(true);
        setSignals([]);
        setTotal(0);
      } else {
        setTierLimit(false);
        setSignals(stampRx(data.items));
        setTotal(data.total || 0);
        setLastScan(new Date().toISOString());
      }
    } catch {
      toast.error("Failed to load signals");
    } finally {
      setLoading(false);
    }
  }, [filter, pipeFilter, direction, instrFilter]);

  useEffect(() => {
    api.get("/brokers")
      .then(({ data }) => {
        const all = (data.items || []).filter((b) => b.status === "live");
        setLiveBrokers(all.map((b) => b.broker || b.name));
        const first = all[0];
        if (first) setLiveBroker(first.broker || first.name);
      })
      .catch(() => { console.warn("Failed to load connected brokers"); });
    api.get("/auth/me")
      .then(({ data }) => { if (data.role === "admin") setIsAdmin(true); })
      .catch(() => {});
    api.get("/admin/platform-config")
      .then(({ data }) => { if (data.scan_interval_minutes) setScanInterval(data.scan_interval_minutes); })
      .catch(() => {});
    api.get("/quant/expectancy?days=30")
      .then(({ data }) => {
        const map = {};
        (data.scanners || []).forEach((s) => { map[s.scanner] = s; });
        setExpectancy(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchSignals(); }, [fetchSignals]);

  useEffect(() => {
    const id = setInterval(fetchSignals, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchSignals]);

  // RU-SIGTAGAGE TA-4: re-render every 15 s so a row's LTP tag ages without a refetch (no network call)
  const [, setAgeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setAgeTick((t) => t + 1), 15 * 1000);
    return () => clearInterval(id);
  }, []);

  // RU-DRIFTUI (DU-3): re-check executability of the cards on screen so Paper/Live re-enable (or disable)
  // as the price moves, without a full reload (no free-tier view counted).
  const onScreenRef = useRef({ view: "active", ids: [] });
  onScreenRef.current = {
    view,
    ids: (view === "watchlist" ? watchSignals : view === "active" ? signals : []).map((s) => s.id ?? s._id),
  };
  const execPollBusy = useRef(false);
  useEffect(() => {
    const id = setInterval(async () => {
      const { view: v, ids } = onScreenRef.current;
      // M2: no poll in a hidden tab, and never two in flight at once
      if (!ids.length || document.hidden || execPollBusy.current) return;
      execPollBusy.current = true;
      try {
        const { data } = await api.get("/signals/executable", { params: { ids: ids.slice(0, 100).join(",") } });
        const items = data?.items;
        const states = data?.states;   // RU-SIGSTATUS SS-7
        const quotes = data?.option_quotes;   // RU-SIGUI2 SU2-2
        if (v === "watchlist") setWatchSignals((l) => mergeExecStatus(l, items, states, quotes));
        else setSignals((l) => mergeExecStatus(l, items, states, quotes));
      } catch {
        /* non-fatal: the card keeps its last verdict; execute still guards server-side */
      } finally {
        execPollBusy.current = false;
      }
    }, EXEC_POLL_MS);
    return () => clearInterval(id);
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const params = { limit: 50 };
      if (filter !== "all")      params.pipeline        = filter;
      if (direction !== "all")   params.direction       = direction;
      if (instrFilter !== "all") params.instrument_type = instrFilter;
      const { data } = await api.get("/signals/history", { params });
      setHistory(stampRx(data.items));
      setHistTotal(data.total || 0);
    } catch {
      toast.error("Failed to load signal history");
    } finally {
      setHistLoading(false);
    }
  }, [filter, pipeFilter, direction, instrFilter]);

  useEffect(() => {
    if (view === "history") fetchHistory();
  }, [view, fetchHistory]);

  const fetchWatchlistSignals = useCallback(async () => {
    setWatchLoading(true);
    try {
      const { data: wlData } = await api.get("/watchlist");
      const inFeed = (wlData.items || [])
        .filter((item) => item.include_in_signals)
        .map((item) => item.symbol);
      setWatchSymbols(inFeed);
      if (inFeed.length === 0) { setWatchSignals([]); return; }
      // fetch all active signals, then filter client-side
      const { data: sigData } = await api.get("/signals", { params: { limit: 100 } });
      const filtered = stampRx(sigData.items).filter((s) => inFeed.includes(s.symbol));
      setWatchSignals(filtered);
    } catch {
      toast.error("Failed to load watchlist signals");
    } finally {
      setWatchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "watchlist") fetchWatchlistSignals();
  }, [view, fetchWatchlistSignals]);

  async function triggerScan() {
    setScanning(true);
    try {
      await api.post("/signals/scan", null, { params: { pipeline: "all" } });
      toast.success("All pipelines queued — results appear in ~2 minutes");
      setTimeout(fetchSignals, 90_000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
                : Array.isArray(detail) ? detail.map((d) => d.msg || d.type).join(", ")
                : e?.message || "Scan failed";
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  }

  // RU-SIGSTATUS (SS-7): after any Execute result, re-read this signal's execution summary and status
  async function refreshStates(signalId) {
    try {
      const { data } = await api.get("/signals/executable", { params: { ids: signalId } });
      const items = data?.items;
      const states = data?.states;
      const quotes = data?.option_quotes;   // RU-SIGUI2 SU2-2
      setSignals((l) => mergeExecStatus(l, items, states, quotes));
      setWatchSignals((l) => mergeExecStatus(l, items, states, quotes));
    } catch {
      /* non-fatal: the 15 s poll retries */
    }
  }

  // SS-7: the Execute result is returned unchanged; once it settles (any result) the card's state is re-read
  function refreshWhenSettled(signalId, pending) {
    Promise.resolve(pending).catch(() => {}).finally(() => refreshStates(signalId));
    return pending;
  }

  function handleExecute(signalId, mode, qty, broker, previewToken) {
    return refreshWhenSettled(signalId, run());
    function run() {
      return executeSignal(signalId, mode, qty, broker, previewToken, liveBroker);
    }
  }

  const groupBtns = [
    { key: "all",       label: "All Groups",  desc: "All 9 scanners" },
    { key: "trend",     label: "Trend",       desc: "Momentum · ORB · Breakout" },
    { key: "reversion", label: "Reversion",   desc: "Gap · VWAP · Mean Rev" },
    { key: "smc",       label: "SMC",         desc: "FVG/OB · Liq. Sweep" },
    { key: "options",   label: "Options OI",  desc: "Trap signal" },
  ];

  const pipelineBtns = [
    { key: "all",             label: "All Scanners" },
    { key: "trap",            label: "Trap" },
    { key: "momentum",        label: "Momentum" },
    { key: "commodity_gap",   label: "Gap" },
    { key: "orb",             label: "ORB" },
    { key: "vwap",            label: "VWAP" },
    { key: "mean_reversion",  label: "MR" },
    { key: "breakout",        label: "Breakout" },
    { key: "fvg_ob",          label: "FVG/OB" },
    { key: "liquidity_sweep", label: "Liq. Sweep" },
  ];

  const filterBarClass = "flex gap-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm p-1 flex-wrap";
  const filterBtnClass = (active) =>
    `px-2 sm:px-3 py-1 text-xs font-section rounded-sm transition-colors ${
      active
        ? "bg-[var(--text-primary)] text-[var(--bg-page)]"
        : "txt-secondary hover:text-[var(--text-primary)]"
    }`;

  return (
    <AppShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4 sm:space-y-6">
        <PageHeader
          overline="Signal Engine"
          title="Signal Feed"
          description={`${total} active signals · scanner runs every ${scanInterval} min`}
          actions={
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={fetchSignals}
                disabled={loading}
                className="rounded-none border-[var(--border)] txt-secondary hover:text-[var(--text-primary)]"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                size="sm"
                onClick={triggerScan}
                disabled={scanning}
                className="rounded-none bg-orange-600 hover:bg-orange-500 text-white font-section tracking-wider"
              >
                {scanning
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Brain className="w-3.5 h-3.5 mr-1.5" />}
                <span className="hidden sm:inline">SCAN NOW</span>
                <span className="sm:hidden">Scan</span>
              </Button>
            </div>
          }
        />

        {/* Signal Engine Dashboard */}
        <SignalDashboard
          signals={signals}
          expectancy={expectancy}
          total={total}
          scanInterval={scanInterval}
          lastScan={lastScan}
        />

        {/* Active / History / Watchlist toggle */}
        <div className="flex gap-1 border-b border-[var(--border)] mb-1">
          {[
            { key: "active",    label: "ACTIVE SIGNALS" },
            { key: "watchlist", label: "WATCHLIST" },
            { key: "history",   label: "HISTORY" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`px-4 py-2 text-xs font-section tracking-wider border-b-2 transition-colors ${
                view === key
                  ? "border-[var(--text-primary)] text-[var(--text-primary)]"
                  : "border-transparent txt-muted hover:text-[var(--text-primary)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Usage meter for free tier */}
        <div className="flex items-center justify-between">
          <UsageMeter resource="signals_per_day" label="signal views today" />
        </div>

        {/* Filters */}
        <div className="space-y-2">
          {/* Group filter — primary */}
          <div className={filterBarClass}>
            {groupBtns.map(({ key, label, desc }) => (
              <button
                key={key}
                onClick={() => { setFilter(key); setPipeFilter("all"); }}
                className={filterBtnClass(filter === key)}
                title={desc}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Individual scanner filter — secondary, shown when group is "all" */}
          {filter === "all" && (
            <div className={filterBarClass}>
              {pipelineBtns.map(({ key, label }) => {
                const exp = expectancy[key];
                const wr = exp?.win_rate_pct;
                const wrColor = wr >= 60 ? "text-green-400" : wr >= 45 ? "text-yellow-400" : "text-red-400";
                return (
                  <button key={key} onClick={() => setPipeFilter(key)} className={filterBtnClass(pipeFilter === key)}>
                    <span>{label}</span>
                    {wr != null && key !== "all" && (
                      <span className={`ml-1 text-[9px] font-mono ${wrColor}`}>{wr.toFixed(0)}%</span>
                    )}
                  </button>
                );
              })}
              {pipeFilter !== "all" && (
                <ScannerLink scanner={pipeFilter} testid="filter-scanner-link"
                             className="px-2 sm:px-3 py-1 text-xs font-section text-[var(--accent,#60a5fa)] whitespace-nowrap">
                  Performance →
                </ScannerLink>
              )}
            </div>
          )}

          {/* Direction + Instrument type */}
          <div className="flex gap-2 flex-wrap">
            <div className={filterBarClass}>
              {["all", "BULLISH", "BEARISH"].map((d) => (
                <button key={d} onClick={() => setDirection(d)} className={filterBtnClass(direction === d)}>
                  {d === "all" ? "All" : d === "BULLISH" ? "Bull" : "Bear"}
                </button>
              ))}
            </div>
            <div className={filterBarClass}>
              {[
                { key: "all", label: "All Types" },
                { key: "EQ",  label: "Equity" },
                { key: "FUT", label: "Futures" },
                { key: "OPT", label: "Options" },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => setInstrFilter(key)} className={filterBtnClass(instrFilter === key)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Signal list — Active */}
        {view === "active" && (loading ? (
          <div className="flex items-center justify-center h-48 txt-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading signals…
          </div>
        ) : tierLimit ? (
          <div className="panel p-8 flex flex-col items-center text-center border border-indigo-500/30 bg-indigo-500/5">
            <Zap className="w-8 h-8 text-indigo-400 mb-3" />
            <div className="text-sm font-section text-[var(--text-primary)] mb-1">Daily limit reached</div>
            <div className="txt-muted text-xs mb-4">Free plan includes 3 signal views per day.</div>
            <a
              href="/pricing"
              className="px-5 py-2 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-section tracking-wider transition-colors"
            >
              Upgrade to Pro — unlimited signals
            </a>
          </div>
        ) : signals.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Target className="w-10 h-10 txt-muted mb-3" />
            <div className="txt-secondary font-section">No active signals</div>
            <div className="txt-muted text-sm mt-1 px-4">
              Signals are generated every {scanInterval} min during market hours (09:15–15:30 IST).
              {isAdmin && <><br />Use "Scan Now" to trigger immediately.</>}
            </div>
          </div>
        ) : (
          <SignalListBoundary>
            <div className="space-y-3">
              {signals.map((s) => (
                <SignalCard key={s.id ?? s._id} signal={s} onExecute={handleExecute} expectancy={expectancy} liveBrokers={liveBrokers} defaultBroker={liveBroker} watch={watches[s.id ?? s._id]} onWatch={handleWatch} onCancelWatch={handleCancelWatch} />
              ))}
            </div>
          </SignalListBoundary>
        ))}

        {/* Watchlist signals */}
        {view === "watchlist" && (watchLoading ? (
          <div className="flex items-center justify-center h-48 txt-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading watchlist signals…
          </div>
        ) : watchSymbols.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Star className="w-10 h-10 txt-muted mb-3" />
            <div className="txt-secondary font-section">No symbols in signal feed</div>
            <div className="txt-muted text-sm mt-1 px-4">
              Go to Watchlist and toggle "Include in Signal Feed" for symbols you want to track here.
            </div>
          </div>
        ) : watchSignals.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Target className="w-10 h-10 txt-muted mb-3" />
            <div className="txt-secondary font-section">No active signals for your watchlist</div>
            <div className="txt-muted text-sm mt-1">
              Tracking {watchSymbols.length} symbol{watchSymbols.length !== 1 ? "s" : ""}: {watchSymbols.join(", ")}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs txt-muted">
              {watchSignals.length} signal{watchSignals.length !== 1 ? "s" : ""} across {watchSymbols.length} watched symbol{watchSymbols.length !== 1 ? "s" : ""}
            </div>
            <SignalListBoundary>
              <div className="space-y-3">
                {watchSignals.map((s) => (
                  <SignalCard key={s.id ?? s._id} signal={s} onExecute={handleExecute} expectancy={expectancy} liveBrokers={liveBrokers} defaultBroker={liveBroker} watch={watches[s.id ?? s._id]} onWatch={handleWatch} onCancelWatch={handleCancelWatch} />
                ))}
              </div>
            </SignalListBoundary>
          </div>
        ))}

        {/* Signal history */}
        {view === "history" && (histLoading ? (
          <div className="flex items-center justify-center h-48 txt-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading history…
          </div>
        ) : history.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Clock className="w-10 h-10 txt-muted mb-3" />
            <div className="txt-secondary font-section">No signal history yet</div>
            <div className="txt-muted text-sm mt-1">Signals older than 4 hours appear here for the past 30 days.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs txt-muted">{histTotal} signals in history</div>
            {history.map((s) => (
              <HistorySignalRow key={s.id} signal={s} />
            ))}
          </div>
        ))}

        <div className="text-xs txt-muted border-t border-[var(--border)] pt-4">
          Active signals are actionable for 4 hours. History is retained for 30 days.
          Paper trades are risk-free. Live trades execute immediately on connected broker.
        </div>
      </div>
    </AppShell>
  );
}
